import type { Box } from 'features/knowledgeDocumentViewer/components/viewer/types'
import { useCallback, useEffect } from 'react'
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'

export const useZoomControls = (
  boxes: Box[],
  imgNatural: { w: number; h: number } | null,
  transformRef: React.RefObject<ReactZoomPanPinchRef | null>,
  resetToFit?: () => void,
  onResetCallback?: () => void
) => {
  const zoomToBox = useCallback(
    (id: string, zoomLevel = 1.5) => {
      const box = boxes.find((b) => b.id === id)
      if (!box || !imgNatural || !transformRef.current) return
      const { x, y, w, h } = box

      const wrapper = transformRef.current.instance.wrapperComponent
      if (!wrapper) return
      const wrapRect = wrapper.getBoundingClientRect()

      const currentState = transformRef.current.instance.transformState
      const contentComponent = transformRef.current.instance.contentComponent
      if (!contentComponent) return

      const contentRect = contentComponent.getBoundingClientRect()

      // Calculate the initial scale factor (image pixels to screen pixels at scale:1)
      const currentScale = currentState.scale
      const contentWidthAtScale1 = contentRect.width / currentScale
      const contentHeightAtScale1 = contentRect.height / currentScale

      const initialScaleX = contentWidthAtScale1 / imgNatural.w
      const initialScaleY = contentHeightAtScale1 / imgNatural.h

      // Center of the box in image coordinates
      const boxCenterX = x + w / 2
      const boxCenterY = y + h / 2

      // Convert box center from image coordinates to display coordinates at scale:1
      const boxCenterInDisplayX = boxCenterX * initialScaleX
      const boxCenterInDisplayY = boxCenterY * initialScaleY
      const targetScale = zoomLevel

      // Calculate position to center the box at the target zoom level
      const positionX = wrapRect.width / 2 - boxCenterInDisplayX * targetScale
      const positionY = wrapRect.height / 2 - boxCenterInDisplayY * targetScale

      transformRef.current.setTransform(positionX, positionY, targetScale, 200)
    },
    [boxes, imgNatural, transformRef]
  )

  const resetZoom = useCallback(() => {
    if (resetToFit) {
      resetToFit()
    } else if (transformRef.current) {
      transformRef.current.resetTransform(200)
    }
    onResetCallback?.()
  }, [resetToFit, transformRef, onResetCallback])

  // Add keyboard listener for spacebar to reset zoom
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if spacebar is pressed and not in an input/textarea
      if (
        event.code === 'Space' &&
        event.target instanceof HTMLElement &&
        !['INPUT', 'TEXTAREA'].includes(event.target.tagName)
      ) {
        event.preventDefault()
        resetZoom()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [resetZoom])

  return { zoomToBox, resetZoom }
}
